// Yield & cost math — docs/ARCHITECTURE_M0.md §5.6.

import type { NodeDef, MarketDef, ModelConstants } from "../types.js";

/** Industry-baseline defect density (defects/cm²) — what the foundry's best-practiced customers see. */
export function baselineDefectDensity(node: NodeDef, m: number): number {
  const age = Math.max(0, m - node.availableMonth);
  return node.d0Mature + (node.d0Intro - node.d0Mature) * Math.exp(-age / node.d0TauMonths);
}

/** YOUR defect density: baseline × a persistent company gap that only yield research closes.
 *  Without research you run (1+gapIntro)× worse than baseline on this node — forever. */
export function defectDensity(node: NodeDef, m: number, researchEm: number, models: ModelConstants): number {
  const gap = 1 + models.yieldGapIntro * Math.exp(-Math.max(0, researchEm) / models.yieldGapTauEm);
  return baselineDefectDensity(node, m) * gap;
}

/** Negative-binomial die yield. areaMm2 in mm²; d0 in defects/cm². */
export function dieYield(areaMm2: number, d0: number, alpha: number): number {
  const areaCm2 = areaMm2 / 100;
  return Math.pow(1 + (areaCm2 * d0) / alpha, -alpha);
}

/** Gross die per wafer (standard edge-loss approximation). */
export function diePerWafer(waferDiameterMm: number, areaMm2: number): number {
  const d = waferDiameterMm;
  const gross = (Math.PI * (d / 2) * (d / 2)) / areaMm2 - (Math.PI * d) / Math.sqrt(2 * areaMm2);
  return Math.max(1, Math.floor(gross));
}

/** Processed wafer cost at monthIndex m (declines from start toward mature). */
export function waferCost(node: NodeDef, m: number): number {
  const age = Math.max(0, m - node.availableMonth);
  return node.waferCostMature + (node.waferCostStart - node.waferCostMature) * Math.exp(-age / node.waferCostTauMonths);
}

/** Die area in mm² for a given complexity on a node. */
export function dieArea(kGates: number, node: NodeDef, models: ModelConstants): number {
  return ((kGates * 1000) / node.gateDensity) * (1 + models.dieOverhead);
}

export interface UnitCostBreakdown {
  d0: number;
  yieldFraction: number;
  diePerWafer: number;
  waferCost: number;
  dieCost: number;
  unitCost: number;
}

export function unitCost(
  node: NodeDef,
  market: MarketDef,
  areaMm2: number,
  m: number,
  researchEm: number,
  models: ModelConstants,
  waferCostMult = 1, // foundry-relationship perks/penalties + wafer discounts (M3)
): UnitCostBreakdown {
  const d0 = defectDensity(node, m, researchEm, models);
  const y = dieYield(areaMm2, d0, models.yieldAlpha);
  const dpw = diePerWafer(node.waferDiameterMm, areaMm2);
  const wc = waferCost(node, m) * waferCostMult;
  const dieCost = wc / (dpw * y);
  return {
    d0,
    yieldFraction: y,
    diePerWafer: dpw,
    waferCost: wc,
    dieCost,
    unitCost: dieCost + market.testCostPerUnit + market.packageCostPerUnit,
  };
}
